import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDashboardMetrics } from "@/hooks/useDashboardMetrics";
import { useApprovalCounts } from "@/hooks/useApprovalCounts";
import { useNotifications } from "@/hooks/useNotifications";
import { useQBStatus } from "@/hooks/useQBStatus";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import type { ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  FolderOpen, DollarSign, TrendingUp, ClipboardCheck,
  Wifi, WifiOff, Receipt, AlertTriangle, FileText, Eye, ArrowRight,
} from "lucide-react";
import { PersonnelDashboardWidget } from "@/components/controller/personnel/PersonnelDashboardWidget";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const pctFmt = (n: number) => `${n.toFixed(1)}%`;

export const ControllerDashboardView = () => {
  const { user } = useAuth();
  const navigate = useTabNavigate();
  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics();
  const { data: approvals, isLoading: appLoading } = useApprovalCounts();
  const { data: notifications = [] } = useNotifications();
  const qbStatus = useQBStatus();

  // Active projects count
  const { data: projectCount } = useQuery({
    queryKey: ["active_project_count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("projects")
        .select("id", { count: "exact", head: true })
        .eq("status", "active");
      return count ?? 0;
    },
    enabled: !!user,
  });

  // Estimate status counts
  const { data: estimateCounts } = useQuery({
    queryKey: ["estimate_status_counts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("estimates").select("status, viewed_at, share_token_expires_at");
      if (error) throw error;
      const counts = { draft: 0, sent: 0, viewed: 0, accepted: 0, rejected: 0, expiring: 0 };
      const now = new Date();
      const sevenDays = new Date(now.getTime() + 7 * 86400000);
      (data ?? []).forEach((e: any) => {
        if (e.status === "Draft") counts.draft++;
        else if (e.status === "Sent") {
          counts.sent++;
          if (e.viewed_at) counts.viewed++;
          if (e.share_token_expires_at && new Date(e.share_token_expires_at) <= sevenDays) counts.expiring++;
        }
        else if (e.status === "Accepted") counts.accepted++;
        else if (e.status === "Rejected") counts.rejected++;
      });
      return counts;
    },
    enabled: !!user,
  });

  // Pending approval notifications
  const pendingItems = notifications.filter(
    (n) => n.status === "unread" && n.type === "approval_needed"
  );

  const totalApprovals = (approvals?.purchaseOrders ?? 0) + (approvals?.jobOrders ?? 0) +
    (approvals?.changeOrders ?? 0) + (approvals?.invoices ?? 0);

  const margin = metrics
    ? metrics.forecastContract > 0
      ? ((metrics.forecastContract - metrics.committedCost) / metrics.forecastContract) * 100
      : 0
    : 0;

  const kpis = [
    { label: "Active Projects", value: projectCount ?? 0, icon: FolderOpen, fmt: (v: number) => String(v) },
    { label: "Revenue This Month", value: metrics?.recognizedRevenue ?? 0, icon: DollarSign, fmt },
    { label: "Gross Margin", value: margin, icon: TrendingUp, fmt: pctFmt,
      color: margin >= 20 ? "text-emerald-600" : margin >= 10 ? "text-amber-600" : "text-destructive" },
    { label: "Pending Approvals", value: totalApprovals, icon: ClipboardCheck, fmt: (v: number) => String(v),
      color: totalApprovals > 0 ? "text-amber-600" : "text-emerald-600" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Controller Dashboard</h1>

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{k.label}</CardTitle>
              <k.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold", k.color)}>
                {metricsLoading || appLoading ? "…" : k.fmt(k.value)}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Two column layout */}
      <div className="grid gap-6 lg:grid-cols-5">
        {/* Left column - 60% */}
        <div className="lg:col-span-3 space-y-6">
          {/* Pending Approvals */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Pending Approvals</CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate("/controller/approvals")}>
                View All
              </Button>
            </CardHeader>
            <CardContent>
              {totalApprovals === 0 ? (
                <div className="py-6 text-center text-muted-foreground">✅ All caught up</div>
              ) : (
                <div className="space-y-2">
                  {[
                    { label: "Purchase Orders", count: approvals?.purchaseOrders ?? 0, path: "/controller/approvals" },
                    { label: "Job Orders", count: approvals?.jobOrders ?? 0, path: "/controller/approvals" },
                    { label: "Change Orders", count: approvals?.changeOrders ?? 0, path: "/controller/approvals" },
                    { label: "Invoices", count: approvals?.invoices ?? 0, path: "/controller/invoices" },
                  ]
                    .filter((i) => i.count > 0)
                    .map((i) => (
                      <div
                        key={i.label}
                        className="flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => navigate(i.path)}
                      >
                        <span className="text-sm">🟡</span>
                        <span className="flex-1 text-sm font-medium">{i.label}</span>
                        <Badge variant="secondary">{i.count}</Badge>
                        <Button variant="ghost" size="sm">Review →</Button>
                      </div>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {notifications.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No recent activity</p>
              ) : (
                <div className="space-y-3">
                  {notifications.slice(0, 8).map((n) => (
                    <div key={n.id} className="flex items-start gap-3">
                      <span className="text-xs mt-1">
                        {n.priority === "urgent" ? "🔴" : n.priority === "high" ? "🟡" : "🔵"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{n.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column - 40% */}
        <div className="lg:col-span-2 space-y-6">
          {/* QB Status */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">QuickBooks Status</CardTitle>
              {qbStatus.connected ? (
                <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-200">
                  <Wifi className="h-3 w-3 mr-1" /> Connected
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <WifiOff className="h-3 w-3 mr-1" /> Disconnected
                </Badge>
              )}
            </CardHeader>
            <CardContent>
              {qbStatus.lastSyncedAt && (
                <p className="text-xs text-muted-foreground">
                  Last synced {formatDistanceToNow(new Date(qbStatus.lastSyncedAt), { addSuffix: true })}
                </p>
              )}
              <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => navigate("/controller/settings")}>
                Manage Integration
              </Button>
            </CardContent>
          </Card>

          {/* Estimates Pipeline */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Estimates Pipeline</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="space-y-2">
              {estimateCounts ? (
                <>
                  {[
                    { label: "Draft", count: estimateCounts.draft, color: "text-muted-foreground" },
                    { label: "Sent", count: estimateCounts.sent, color: "text-blue-600" },
                    { label: "Viewed", count: estimateCounts.viewed, color: "text-blue-600", icon: <Eye className="h-3 w-3" /> },
                    { label: "Accepted", count: estimateCounts.accepted, color: "text-emerald-600", action: true },
                    { label: "Expiring (7d)", count: estimateCounts.expiring, color: "text-amber-600" },
                  ].filter(i => i.count > 0).map((i) => (
                    <div key={i.label} className="flex items-center justify-between cursor-pointer hover:bg-muted/50 rounded px-2 py-1 -mx-2" onClick={() => navigate("/controller/estimates")}>
                      <span className={`text-sm flex items-center gap-1 ${i.color}`}>{i.icon}{i.label}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{i.count}</Badge>
                        {i.action && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                      </div>
                    </div>
                  ))}
                  {estimateCounts.draft === 0 && estimateCounts.sent === 0 && estimateCounts.accepted === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">No active estimates</p>
                  )}
                </>
              ) : <p className="text-xs text-muted-foreground">Loading…</p>}
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: "Forecast Contract", value: metrics?.forecastContract ?? 0 },
                { label: "Committed Cost", value: metrics?.committedCost ?? 0 },
                { label: "Actual Cost", value: metrics?.actualCost ?? 0 },
                { label: "Draft Pipeline", value: metrics?.draftPipeline ?? 0 },
                { label: "Recognized Revenue", value: metrics?.recognizedRevenue ?? 0 },
              ].map((i) => (
                <div key={i.label} className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{i.label}</span>
                  <span className="text-sm font-semibold">{metricsLoading ? "…" : fmt(i.value)}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Personnel Projects Widget */}
          <PersonnelDashboardWidget />
        </div>
      </div>
    </div>
  );
};
